/**
 * WebAuthn ceremonies against Better Auth's passkey plugin. Staff register a passkey after a
 * magic-link session, then sign in with it on later visits (AUT-005).
 */
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";

export type PasskeyOutcome = "ok" | "unsupported" | "refused" | "cancelled" | "failed";

function cancelled(error: Error): boolean {
  return error.name === "NotAllowedError";
}

async function readCreationOptions(
  response: Response,
): Promise<PublicKeyCredentialCreationOptionsJSON | null> {
  if (response.status === 401 || response.status === 403) return null;

  if (!response.ok) return null;

  const body: PublicKeyCredentialCreationOptionsJSON | null = await response
    .json()
    .catch(() => null);

  return body?.challenge ? body : null;
}

async function readRequestOptions(
  response: Response,
): Promise<PublicKeyCredentialRequestOptionsJSON | null> {
  if (!response.ok) return null;

  const body: PublicKeyCredentialRequestOptionsJSON | null = await response
    .json()
    .catch(() => null);

  return body?.challenge ? body : null;
}

async function postCredential(
  path: string,
  credential: RegistrationResponseJSON | AuthenticationResponseJSON,
): Promise<boolean> {
  const verified = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ response: credential }),
  });

  return verified.ok;
}

export async function registerPasskey(): Promise<PasskeyOutcome> {
  if (!browserSupportsWebAuthn()) return "unsupported";
  const options = await fetch("/api/auth/passkey/generate-register-options");

  if (options.status === 401 || options.status === 403) return "refused";
  const creation = await readCreationOptions(options);

  if (creation === null) return "failed";

  try {
    const credential = await startRegistration({ optionsJSON: creation });

    return (await postCredential("/api/auth/passkey/verify-registration", credential))
      ? "ok"
      : "failed";
  } catch (error) {
    return error instanceof Error && cancelled(error) ? "cancelled" : "failed";
  }
}

export async function signInWithPasskey(): Promise<PasskeyOutcome> {
  if (!browserSupportsWebAuthn()) return "unsupported";
  const options = await fetch("/api/auth/passkey/generate-authenticate-options");
  const request = await readRequestOptions(options);

  if (request === null) return "failed";

  try {
    const credential = await startAuthentication({ optionsJSON: request });

    return (await postCredential("/api/auth/passkey/verify-authentication", credential))
      ? "ok"
      : "failed";
  } catch (error) {
    return error instanceof Error && cancelled(error) ? "cancelled" : "failed";
  }
}
